import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button, Header, Nav, ResponsiveContext } from 'grommet';
import { CircleQuestion, Refresh } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { ApiError } from '/src/api';
import {
  AnchorLink,
  Box,
  ClientsDropdown,
  FuseLogo,
  Link,
  ModelAuditLogDropdown,
  SearchDropdown,
  Text,
  UserProfileMenu,
} from '/src/components';
import { config } from '/src/config';
import { useClientStore, useGlobalStore, useModelAuditLogStore, useUserStore } from '/src/context';
import { toastMessages } from '/src/lib/toast';
import { tooltipMessages } from '/src/lib/tooltips';
import { AuditorType, ModelAuditLogState } from '/src/lib/types';
import { pxToRem } from '/src/utils';
import { getTourNameFromPath, resetTour, startTour } from '/src/utils/productTours';

export const HeaderNav: React.FC<HeaderNavProps> = observer((props) => {
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const clientStore = useClientStore();
  const modelAuditLogStore = useModelAuditLogStore();
  const location = useLocation();
  const screenSize = useContext(ResponsiveContext);
  const isMobile = useMemo(() => screenSize === 'small', [screenSize]);
  const tooltips = tooltipMessages();
  const { t } = useTranslation();

  const [hoverItem, setHoverItem] = useState<string>();

  const clientId = userStore.isExternalUser ? clientStore.activeClientId : clientStore.selectedClientId;

  useEffect(() => {
    modelAuditLogStore
      .fetchModelAuditLogs({
        action_taken_by: AuditorType.ExternalUser,
        model_audit_log_state_id: ModelAuditLogState.Unresolved,
      })
      .catch((err) => globalStore.handleApiError(err as ApiError, toastMessages.fetchAuditLog.error));
  }, [location]);

  const selectedItem = useMemo(() => {
    if (location.pathname.includes(`/clients/${clientId}/facilities`)) return 'Facilities';
    else if (location.pathname.includes(`/clients/${clientId}/users`)) return 'Users';
    else if (location.pathname.includes(`/clients/${clientId}/statements`)) return 'Statements';
    else if (location.pathname.includes(`/clients/${clientId}`)) return 'Company';
    else if (location.pathname.includes('/clients')) return 'Clients';
    else if (location.pathname.includes('/users')) return 'Users';
    else if (location.pathname.includes('/api-data-old')) return 'API Data [Old]';
    else if (location.pathname.includes('/api-data')) return 'API Data [New]';
    else if (location.pathname === '/reports' || location.pathname.startsWith('/reports/'))
      return t('navigation:reports');
    else if (location.pathname.includes('/fuse-admin')) return 'FuSE Admin';
    else if (location.pathname === '/') return t('navigation:dashboard');
  }, [location.pathname, clientId, t]);

  const renderLinkItem = useCallback(
    (url: string, label: string, leftBorderSize?: string) => {
      const isSelected = selectedItem === label;
      const isHover = hoverItem === label;
      const dataTourAttr = `nav-${label.toLowerCase().replace(/\s+/g, '-')}`;

      return (
        <Box
          key={label}
          // animation={{ type: 'fadeIn', duration: 300 }}
          direction="row"
          border={{ side: 'left', size: leftBorderSize || '0.75px', color: 'light-4' }}
          width={{ min: isMobile ? '90px' : '115px' }}
          justify="center"
          align="center"
          style={{
            boxShadow: isSelected || isHover ? 'rgba(24, 100, 158, 0.3) 0px -24px 24px -24px inset' : '',
          }}
          transitionProperty="color, box-shadow"
          transitionDuration="0.15s, 0.15s"
          transitionTimingFunction="ease, ease"
          onMouseOver={() => setHoverItem(label)}
          onMouseOut={() => setHoverItem(undefined)}
          data-tour={dataTourAttr}
        >
          <Link
            to={url}
            style={{
              display: 'flex',
              flex: 1,
              alignSelf: 'stretch',
              alignItems: 'center',
              justifyContent: 'center',
              padding: isMobile ? '1rem 0.5rem' : '1rem 0.875rem',
            }}
          >
            <Box
              transitionProperty="padding-bottom"
              transitionDuration="0.15s"
              transitionTimingFunction="ease"
              pad={{ bottom: isHover ? '3px' : undefined }}
            >
              <Text
                size={
                  isMobile ? (label === 'FuSE Admin' || label === 'API Data' ? pxToRem(14) : pxToRem(16)) : pxToRem(18)
                }
                lineHeight={isMobile ? pxToRem(16) : pxToRem(18)}
                weight={300}
                color={!isSelected || isHover ? 'accent-2' : 'accent-1'}
                style={{ whiteSpace: label === 'FuSE Admin' || label === 'API Data' ? 'nowrap' : 'normal' }}
              >
                {label}
              </Text>
            </Box>
          </Link>
        </Box>
      );
    },
    [selectedItem, hoverItem, setHoverItem, isMobile]
  );

  const linkItems = useMemo<Record<string, boolean | string>[]>(
    () => [
      // Internal users
      { shouldShow: userStore.isInternalUser, url: '/', label: t('navigation:dashboard') },
      { shouldShow: userStore.isInternalUser, url: '/clients', label: t('navigation:clients') },
      { shouldShow: userStore.isInternalUser, url: '/users', label: t('navigation:users') },
      { shouldShow: userStore.isFuseAdmin, url: '/api-data', label: t('navigation:apiData') + ' [New]' },
      { shouldShow: userStore.isFuseAdmin, url: '/api-data-old', label: t('navigation:apiData') + ' [Old]' },
      { shouldShow: userStore.isInternalUser, url: '/fuse-admin/importer', label: t('navigation:fuseAdmin') },
      { shouldShow: userStore.isFuseAdmin, url: '/reports', label: t('navigation:reports') },

      // External users
      { shouldShow: userStore.isExternalUser, url: '/', label: t('navigation:dashboard') },
      { shouldShow: userStore.isClientUser && !!clientId, url: `/clients/${clientId}`, label: t('navigation:company') },
      {
        shouldShow: userStore.isExternalUser && !!clientId,
        url: `/clients/${clientId}/facilities`,
        label: t('navigation:facilities'),
      },
      {
        shouldShow: userStore.isClientAdmin && !!clientId,
        url: `/clients/${clientId}/users`,
        label: t('navigation:users'),
      },
      {
        shouldShow: userStore.isClientUser && !!clientId,
        url: `/clients/${clientId}/statements`,
        label: t('navigation:statements'),
      },
      { shouldShow: userStore.isClientAdmin, url: '/reports', label: t('navigation:reports') },
    ],
    [
      userStore.isInternalUser,
      userStore.isFuseAdmin,
      userStore.isExternalUser,
      userStore.isClientAdmin,
      userStore.isClientUser,
      clientId,
      t,
    ]
  );

  const visibleLinkItems = useMemo(() => linkItems.filter(({ shouldShow }) => shouldShow), [linkItems]);

  const currentTourName = useMemo(() => {
    return getTourNameFromPath(location.pathname, clientId);
  }, [location.pathname, clientId]);

  const handleResetTour = () => {
    if (currentTourName) {
      resetTour(currentTourName);
      const tourNameWithoutSuffix = currentTourName.replace('-tour', '') as
        | 'dashboard'
        | 'facilities-list'
        | 'facility-details'
        | 'equipment-list'
        | 'equipment-details'
        | 'users'
        | 'company'
        | 'statements';
      startTour(tourNameWithoutSuffix);
    }
  };

  const TourResetButton = () => {
    if (!currentTourName || userStore.isFuseAdmin) return null;

    return (
      <Box width="20px" height="20px" alignSelf="center">
        <Button
          plain
          icon={<Refresh size="20" />}
          onClick={handleResetTour}
          tip={t('common:restartPageTour')}
          data-tour="tour-reset-button"
        />
      </Box>
    );
  };

  const HelpButton = () => (
    <Box width="20px" height="20px" alignSelf="center">
      <Link to={config.urls.fuseSupportUrl} target="_blank">
        <Button plain icon={<CircleQuestion size="20" />} tip={tooltips.support} data-tour="support-page-button" />
      </Link>
    </Box>
  );

  return (
    <Header
      gridArea={props.gridArea}
      direction="row"
      animation={!globalStore.didAnimateHeader ? { type: 'slideDown', duration: 300, size: 'large' } : undefined}
      onAnimationEnd={() => globalStore.setDidAnimateHeader(true)}
    >
      {userStore.user && (
        <Nav
          animation={!globalStore.didAnimateHeader ? { type: 'fadeIn', duration: 300 } : undefined}
          direction="row"
          background="white"
          flex="grow"
          pad={{ horizontal: isMobile ? '1rem' : visibleLinkItems.length > 6 ? '5rem' : '7.2rem' }}
          border={{ color: 'brand', side: 'bottom', size: '4px' }}
        >
          <Box direction="row" align="center" gap="small">
            <AnchorLink to="/" icon={<FuseLogo height="40px" />} alignSelf="center" />
            {process.env.NODE_ENV === 'development' && screenSize !== 'small' && (
              <Text color="red" weight="bold" size="large">
                DEV
              </Text>
            )}
          </Box>
          <Box
            direction="row"
            border={{ side: 'right', size: '0.75px', color: 'light-4' }}
            style={{ overflowX: isMobile ? 'auto' : 'visible' }}
          >
            {visibleLinkItems.map(({ url, label }) => renderLinkItem(url as string, label as string))}
          </Box>
          <Box flex="grow" />
          {location.pathname === '/' && !userStore.isClientUser && !userStore.isFacilityUser && !isMobile && (
            <ClientsDropdown />
          )}
          {location.pathname !== '/search' && !isMobile && <SearchDropdown />}
          {userStore.isFuseAdmin && !isMobile && <ModelAuditLogDropdown />}
          <HelpButton />
          <TourResetButton />
          <UserProfileMenu margin={{ right: isMobile ? '0.5rem' : '0.875rem' }} />
        </Nav>
      )}
    </Header>
  );
});

export type HeaderNavProps = {
  gridArea?: string;
};
